import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import {
    Briefcase,
    MapPin,
    Calendar,
    Clock,
    DollarSign,
    AlertCircle,
    Eye,
    CheckCircle,
    XCircle,
    Loader as LoaderIcon
} from 'lucide-react';

export default function MyAssignedJobs() {
    const { currentUser } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, active, completed

    // Real-time listener for assigned jobs
    useEffect(() => {
        if (!currentUser) return;

        const q = query(
            collection(db, 'workOrders'),
            where('contractorId', '==', currentUser.uid),
            orderBy('updatedAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const assignedJobs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setJobs(assignedJobs);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    const getStatusColor = (status) => {
        const colors = {
            'assigned': 'bg-blue-100 text-blue-800',
            'in-progress': 'bg-yellow-100 text-yellow-800',
            'completed': 'bg-green-100 text-green-800',
            'cancelled': 'bg-red-100 text-red-800',
            'pending': 'bg-gray-100 text-gray-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const getStatusIcon = (status) => {
        const icons = {
            'assigned': Briefcase,
            'in-progress': LoaderIcon,
            'completed': CheckCircle,
            'cancelled': XCircle
        };
        const Icon = icons[status] || Briefcase;
        return Icon;
    };

    const filteredJobs = jobs.filter(job => {
        if (filter === 'active') {
            return job.status === 'assigned' || job.status === 'in-progress';
        }
        if (filter === 'completed') {
            return job.status === 'completed';
        }
        return true; // 'all'
    });

    const stats = {
        total: jobs.length,
        active: jobs.filter(j => j.status === 'assigned' || j.status === 'in-progress').length,
        completed: jobs.filter(j => j.status === 'completed').length
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading your jobs...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        My Assigned Jobs
                    </h1>
                    <p className="text-gray-600 mt-2">
                        Manage and track your work assignments
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Jobs</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
                            </div>
                            <Briefcase className="w-10 h-10 text-blue-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Active</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.active}</p>
                            </div>
                            <LoaderIcon className="w-10 h-10 text-yellow-600" />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Completed</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.completed}</p>
                            </div>
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-6 flex items-center space-x-2">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            filter === 'all'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        All ({stats.total})
                    </button>
                    <button
                        onClick={() => setFilter('active')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            filter === 'active'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        Active ({stats.active})
                    </button>
                    <button
                        onClick={() => setFilter('completed')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            filter === 'completed'
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                        Completed ({stats.completed})
                    </button>
                </div>

                {/* Jobs List */}
                {filteredJobs.length > 0 ? (
                    <div className="space-y-4">
                        {filteredJobs.map((job, index) => {
                            const StatusIcon = getStatusIcon(job.status);

                            return (
                                <motion.div
                                    key={job.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Link
                                        to={`/contractor/jobs/${job.id}`}
                                        className="block bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                {/* Title and Status */}
                                                <div className="flex items-center space-x-3 mb-3">
                                                    <h3 className="text-lg font-bold text-gray-900">
                                                        {job.serviceType}
                                                    </h3>
                                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)} flex items-center space-x-1`}>
                                                        <StatusIcon className="w-3 h-3" />
                                                        <span className="capitalize">{job.status.replace('-', ' ')}</span>
                                                    </span>
                                                </div>

                                                {/* Details Grid */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
                                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                        <MapPin className="w-4 h-4 flex-shrink-0" />
                                                        <span className="truncate">{job.location}</span>
                                                    </div>

                                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                        <Calendar className="w-4 h-4 flex-shrink-0" />
                                                        <span>
                                                            {new Date(job.preferredDate).toLocaleDateString()}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                        <Clock className="w-4 h-4 flex-shrink-0" />
                                                        <span>{job.estimatedHours} hours</span>
                                                    </div>

                                                    {job.contractorPayment && (
                                                        <div className="flex items-center space-x-2 text-sm font-semibold text-green-600">
                                                            <DollarSign className="w-4 h-4 flex-shrink-0" />
                                                            <span>${job.contractorPayment}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Description */}
                                                {job.description && (
                                                    <p className="text-sm text-gray-600 line-clamp-2">
                                                        {job.description}
                                                    </p>
                                                )}

                                                {/* Client Info */}
                                                {job.clientName && (
                                                    <div className="mt-2 text-sm text-gray-500">
                                                        <span className="font-medium">Client:</span> {job.clientName}
                                                    </div>
                                                )}
                                            </div>

                                            {/* View Button */}
                                            <div className="ml-6">
                                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                                    <Eye className="w-5 h-5 text-blue-600" />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {filter === 'all' && 'No Jobs Assigned'}
                            {filter === 'active' && 'No Active Jobs'}
                            {filter === 'completed' && 'No Completed Jobs'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {filter === 'all' && "You don't have any assigned jobs yet."}
                            {filter === 'active' && "You don't have any jobs in progress."}
                            {filter === 'completed' && "You haven't completed any jobs yet."}
                        </p>
                        <Link
                            to="/contractor/available-jobs"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <Briefcase className="w-4 h-4 mr-2" />
                            Browse Available Jobs
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
